/**
 * ================================================================================
 * CENTRAL ROUTING LOGIC - SINGLE SOURCE OF TRUTH
 * ================================================================================
 * 
 * This is the ONLY place where post-login routing decisions are made.
 * All components must use this function to ensure consistency and prevent
 * agencies from accessing private landlord routes and vice versa.
 * 
 * STRICT ROUTING RULES (NON-NEGOTIABLE):
 * - tenant -> "/" (tenant home)
 * - landlord + private -> "/landlord" (private landlord dashboard)
 * - landlord + agency -> "/landlord/agency/dashboard" (agency dashboard)
 * - landlord + no landlordType -> "/landlord-type" (selection screen)
 * 
 * CRITICAL SECURITY:
 * - Agencies NEVER land in private landlord flow
 * - Private landlords NEVER land in agency flow
 * - Missing landlordType always redirects to selection screen
 * 
 * BACKWARD COMPATIBILITY:
 * - Legacy landlords without landlordType default to "private" (lazy-write on login)
 * - New landlords must choose landlordType during onboarding
 */

/**
 * Get the post-login route based on user role and landlordType
 * 
 * This is the SINGLE SOURCE OF TRUTH for all post-login routing.
 * Used by:
 * - Login success handlers
 * - App load when session exists
 * - Role selection completion
 * - LandlordType selection completion
 * 
 * @param {Object} userDoc - Firestore user document data
 * @param {string} userEmail - User email address (optional, for logging)
 * @returns {string} - Route path to redirect to
 */
export function getPostLoginRoute(userDoc, userEmail = '') {
  if (!userDoc) {
    return '/user-type-login';
  }

  const role = userDoc.role || userDoc.userType || userDoc.accountType || userDoc.type;
  const landlordType = userDoc.landlordType;

  if (!role) {
    return '/user-type-login';
  }

  // Tenant routing - always to tenant home
  if (role === 'tenant') {
    return '/';
  }

  // Landlord routing - STRICT branching by landlordType
  if (role === 'landlord') {
    // If landlordType is missing, show selection screen
    // This should not happen for new users (enforced during onboarding)
    // But we handle it gracefully for legacy users
    if (!landlordType) {
      return '/landlord-type';
    }

    // PRIVATE LANDLORD: Route to private landlord dashboard
    // This is the existing private landlord flow - DO NOT CHANGE
    if (landlordType === 'private') {
      return '/landlord';
    }

    // AGENCY: Route to agency dashboard (or onboarding if incomplete)
    // This is the NEW agency flow - completely separate from private
    if (landlordType === 'agency') {
      // Check if agency profile is complete
      const agencyProfile = userDoc.agencyProfile || {};
      const hasCompanyName = agencyProfile.companyName && agencyProfile.companyName.trim().length > 0;
      
      // If profile incomplete, redirect to onboarding
      if (!hasCompanyName) {
        return '/landlord/agency/onboarding';
      }
      
      // Profile complete - go to agency dashboard
      return '/landlord/agency/dashboard';
    }
  }

  return '/user-type-login';
}

/**
 * Check if a user should have access to a route based on their role and landlordType
 * @param {string} routePath - The route path being accessed
 * @param {string} userRole - User's role ("tenant" | "landlord")
 * @param {string} landlordType - User's landlordType ("private" | "agency" | null)
 * @returns {Object} - { allowed: boolean, redirectTo?: string }
 */
export function checkRouteAccess(routePath, userRole, landlordType) {
  // Tenant routes
  if (routePath.startsWith('/') && !routePath.startsWith('/landlord') && !routePath.startsWith('/user-type') && !routePath.startsWith('/welcome') && !routePath.startsWith('/login')) {
    return {
      allowed: userRole === 'tenant',
      redirectTo: userRole === 'landlord' 
        ? (landlordType === 'agency' ? '/landlord/agency/dashboard' : '/landlord')
        : '/user-type-login'
    };
  }

  // Private landlord routes - ONLY for private landlords
  if (routePath.startsWith('/landlord') && !routePath.startsWith('/landlord/agency') && !routePath.startsWith('/landlord-type') && !routePath.startsWith('/landlord/onboarding')) {
    if (userRole !== 'landlord') {
      return {
        allowed: false,
        redirectTo: '/user-type-login'
      };
    }

    // CRITICAL: Agencies MUST NOT access private landlord routes
    if (landlordType === 'agency') {
      return {
        allowed: false,
        redirectTo: '/landlord/agency/dashboard'
      };
    }

    // Private landlords can access
    if (landlordType === 'private') {
      return { allowed: true };
    }

    // No landlordType - redirect to selection
    return {
      allowed: false,
      redirectTo: '/landlord-type'
    };
  }

  // Agency routes - ONLY for agencies
  if (routePath.startsWith('/landlord/agency')) {
    if (userRole !== 'landlord') {
      return {
        allowed: false,
        redirectTo: '/user-type-login'
      };
    }

    // CRITICAL: Private landlords MUST NOT access agency routes
    if (landlordType === 'private') {
      return {
        allowed: false,
        redirectTo: '/landlord'
      };
    }

    // Agencies can access
    if (landlordType === 'agency') {
      return { allowed: true };
    }

    // No landlordType - redirect to selection
    return {
      allowed: false,
      redirectTo: '/landlord-type'
    };
  }

  // Allow access to selection/onboarding routes
  if (routePath === '/landlord-type' || routePath === '/landlord/onboarding' || routePath === '/landlord/agency/onboarding') {
    return { allowed: true };
  }

  // Default: allow (for public routes)
  return { allowed: true };
}

